import type { TypeCurveListingRow } from 'types/api-responses';
import { useListingQuery } from './hooks';

/**
 * Re-exported so callers that seed the DataTable's page size (e.g.
 * `type-curves-content.tsx`) keep a stable import path. The constant and the
 * shared table-state hook now live in `hooks.ts`.
 */
export { DEFAULT_PAGE_SIZE } from './hooks';

export interface TypeCurvesListingFilters {
  // Core TC filters
  active?: boolean;
  hasNewWells?: 'since_created' | 'since_last_qc';
  basin?: string[];
  formation?: string[];
  typeCurveArea?: string[];
  dataSource?: string[];
  status?: string[];
  /**
   * `not started` is the Inventories view's own value — it selects the rows
   * with no verdict, which the response reports as a null verification. The
   * other views only ever send the first two.
   */
  verification?: 'correct' | 'incorrect' | 'not started';
  validation?: 'valid' | 'warning' | 'invalid' | 'incomplete';
  text?: string;
  uniqueTcId?: string;
  createdAt?: { start?: string; end?: string };
  updatedAt?: { start?: string; end?: string };
  sortAttribute?: string;
  sortDirection?: 'ASC' | 'DESC';
  // Scenario filters (Story 1)
  typeCurveScenarioIds?: number[];
  developmentType?: string;
  basinAreaCount?: { min?: number; max?: number };
  dcaScenario?: string[];
  scalingVariables?: string[];
  createdBy?: string[];
  // Promotion & tasks filters (Story 1)
  promoted?: boolean;
  promotedBy?: string[];
  tasksComplete?: boolean;
  /** The Type Curve serving a (scenario, area, formation) zone by default (INS-10547 Story 6). */
  isDefault?: boolean;
  // Inventory Workspaces view — the listing mock's own filter rail.
  updatedBy?: string[];
  state?: string[];
  inventoryPackageName?: string[];
}

export interface TypeCurvesListingFacets {
  basin?: { buckets: Array<{ value: string; count: number }> };
  formation?: { buckets: Array<{ value: string; count: number }> };
  typeCurveAreaName?: { buckets: Array<{ value: string; count: number }> };
  dataLoadSource?: { buckets: Array<{ value: string; count: number }> };
  typeCurveScenario?: {
    buckets: Array<{ value: string; label: string; count: number }>;
  };
  promotedBy?: { buckets: Array<{ value: string; count: number }> };
  status?: { buckets: Array<{ value: string; count: number }> };
  promoted?: { buckets: Array<{ value: boolean; count: number }> };
  tasksComplete?: { buckets: Array<{ value: boolean; count: number }> };
  isDefault?: { buckets: Array<{ value: boolean; count: number }> };
  dcaScenario?: { buckets: Array<{ value: string; count: number }> };
  createdBy?: { buckets: Array<{ value: string; count: number }> };
  developmentType?: { buckets: Array<{ value: string; count: number }> };
  // Inventory Workspaces view — the listing mock's own filter rail.
  updatedBy?: { buckets: Array<{ value: string; count: number }> };
  state?: { buckets: Array<{ value: string; count: number }> };
  inventoryPackageName?: { buckets: Array<{ value: string; count: number }> };
  verification?: { buckets: Array<{ value: string; count: number }> };
}

export function useTypeCurvesListing(
  filters: TypeCurvesListingFilters,
  options: { enabled: boolean }
) {
  return useListingQuery<TypeCurveListingRow>(
    'type_curves',
    'tc-listing',
    filters,
    options
  );
}
